import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import classnames from "classnames";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../Components/Common/HelpArticleLink";
import {
  getHelpArticleUrl,
  HELP_ARTICLE_GOOGLE_BOOKINGS,
  HELP_ARTICLE_SETTINGS_INTEGRATIONS,
} from "../../helpers/helpCenter";
import IntegrationConfigModal from "./IntegrationConfigModal";
import {
  getInstalled,
  getProviders,
  IntegrationProvider,
  TenantIntegration,
} from "../../services/integrationService";
import { getUnits, Unit } from "../../services/unitService";

const CATEGORY_ORDER = ["payments", "terminal", "messaging", "presence"] as const;
const GOOGLE_RESERVE_SLUG = "google-reserve";

type IntegrationCategory = (typeof CATEGORY_ORDER)[number];

const providerIcon = (slug: string): string => {
  switch (slug) {
    case "asaas":
    case "pagbank":
      return "ri-bank-card-line";
    case "mercadopago":
      return "ri-wallet-3-line";
    case "iugis":
      return "ri-whatsapp-line";
    case "google-reserve":
      return "ri-map-pin-user-line";
    default:
      return "ri-plug-line";
  }
};

const categoryIcon = (category: string): string => {
  switch (category) {
    case "payments":
      return "ri-bank-card-line";
    case "terminal":
      return "ri-smartphone-line";
    case "messaging":
      return "ri-whatsapp-line";
    case "presence":
      return "ri-global-line";
    default:
      return "ri-apps-line";
  }
};

interface ProviderCardProps {
  provider: IntegrationProvider;
  providerInstallations: TenantIntegration[];
  onConnect: (provider: IntegrationProvider) => void;
  onConfigure: (
    provider: IntegrationProvider,
    integration: TenantIntegration
  ) => void;
  capabilityLabel: (capability: string) => string;
}

const ProviderCard = ({
  provider,
  providerInstallations,
  onConnect,
  onConfigure,
  capabilityLabel,
}: ProviderCardProps) => {
  const { t } = useTranslation();
  const primaryIntegration = providerInstallations[0] ?? null;
  const isInstalled = providerInstallations.length > 0;
  const isEnabled =
    providerInstallations.some((item) => item.enabled) ?? false;

  return (
    <Col key={provider.slug} xl={4} lg={6} md={6}>
      <Card className="h-100 border border-primary border-opacity-25 shadow-none">
        <CardBody className="d-flex flex-column">
          <div className="d-flex align-items-start justify-content-between mb-3">
            <div className="avatar-sm flex-shrink-0">
              <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-4">
                <i className={providerIcon(provider.slug)} />
              </span>
            </div>
            <div className="d-flex flex-wrap gap-1 justify-content-end">
              {provider.is_recommended && (
                <Badge className="bg-success-subtle text-success">
                  {t("studio.integrations.badge.recommended")}
                </Badge>
              )}
              <Badge
                color={isInstalled ? "success" : "secondary"}
                className={
                  isInstalled
                    ? "bg-success-subtle text-success"
                    : "bg-light text-muted"
                }
              >
                {isInstalled
                  ? t("studio.integrations.status.installed")
                  : t("studio.integrations.status.notInstalled")}
              </Badge>
            </div>
          </div>

          <h5 className="mb-2">{provider.name}</h5>

          {provider.tagline ? (
            <p className="text-muted small mb-3">{provider.tagline}</p>
          ) : null}

          <div className="d-flex flex-wrap gap-1 mb-3">
            {provider.capabilities.map((capability) => (
              <Badge
                key={capability}
                color="info"
                className="bg-info-subtle text-info"
              >
                {capabilityLabel(capability)}
              </Badge>
            ))}
          </div>

          {isInstalled && (
            <p className="text-muted small mb-3">
              {isEnabled
                ? t("studio.integrations.status.enabled")
                : t("studio.integrations.status.disabled")}
              {primaryIntegration?.unit?.name
                ? ` · ${primaryIntegration.unit.name}`
                : ""}
            </p>
          )}

          <div className="mt-auto d-flex flex-wrap gap-2">
            {isInstalled && primaryIntegration ? (
              <>
                <Button
                  color="primary"
                  className="flex-grow-1"
                  onClick={() => onConfigure(provider, primaryIntegration)}
                >
                  {t("studio.integrations.actions.configure")}
                </Button>
                {providerInstallations.length > 1 &&
                  providerInstallations.slice(1).map((integration) => (
                    <Button
                      key={`${integration.capability}-${integration.unit_id ?? "global"}`}
                      color="primary"
                      outline
                      size="sm"
                      onClick={() => onConfigure(provider, integration)}
                    >
                      {capabilityLabel(integration.capability)}
                    </Button>
                  ))}
              </>
            ) : (
              <Button
                color="primary"
                className="w-100"
                onClick={() => onConnect(provider)}
              >
                {t("studio.integrations.actions.connect")}
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

const IntegrationsPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);

  document.title = t("studio.integrations.pageTitle");

  const [providers, setProviders] = useState<IntegrationProvider[]>([]);
  const [installed, setInstalled] = useState<TenantIntegration[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] =
    useState<IntegrationProvider | null>(null);
  const [selectedIntegration, setSelectedIntegration] =
    useState<TenantIntegration | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [deepLinkUnitId, setDeepLinkUnitId] = useState<number | null>(null);
  const [autoClaim, setAutoClaim] = useState(false);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      const [providersResponse, installedResponse, unitsResponse] =
        await Promise.all([
          getProviders(),
          getInstalled(),
          getUnits({ per_page: 100 }),
        ]);

      setProviders(providersResponse.data ?? []);
      setInstalled(installedResponse.data ?? []);
      setUnits(unitsResponse.data ?? []);
    } catch {
      toast.error(t("studio.integrations.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (loading || deepLinkHandled.current || providers.length === 0) {
      return;
    }

    const app = searchParams.get("app");
    if (app !== GOOGLE_RESERVE_SLUG) {
      return;
    }

    const provider = providers.find((item) => item.slug === GOOGLE_RESERVE_SLUG);
    if (!provider) {
      return;
    }

    deepLinkHandled.current = true;

    const unitParam = searchParams.get("unit_id");
    const unitId = unitParam ? Number(unitParam) : null;
    const shouldClaim = searchParams.get("claim") === "1";
    const installations = installed.filter(
      (item) => item.provider_slug === GOOGLE_RESERVE_SLUG
    );
    const match =
      unitId !== null && !Number.isNaN(unitId)
        ? installations.find((item) => item.unit_id === unitId) ?? null
        : installations[0] ?? null;

    setActiveCategory(provider.category || "presence");
    setDeepLinkUnitId(
      unitId !== null && !Number.isNaN(unitId) ? unitId : match?.unit_id ?? null
    );
    setAutoClaim(shouldClaim);
    setSelectedProvider(provider);
    setSelectedIntegration(match);
    setModalOpen(true);

    const next = new URLSearchParams(searchParams);
    next.delete("app");
    next.delete("unit_id");
    next.delete("claim");
    setSearchParams(next, { replace: true });
  }, [loading, providers, installed, searchParams, setSearchParams]);

  const installedByProvider = useMemo(() => {
    const map = new Map<string, TenantIntegration[]>();

    installed.forEach((integration) => {
      const existing = map.get(integration.provider_slug) ?? [];
      existing.push(integration);
      map.set(integration.provider_slug, existing);
    });

    return map;
  }, [installed]);

  const providersByCategory = useMemo(() => {
    const grouped = new Map<string, IntegrationProvider[]>();

    providers.forEach((provider) => {
      const existing = grouped.get(provider.category) ?? [];
      existing.push(provider);
      grouped.set(provider.category, existing);
    });

    grouped.forEach((list, category) => {
      grouped.set(
        category,
        [...list].sort((a, b) => a.sort_order - b.sort_order)
      );
    });

    return grouped;
  }, [providers]);

  const visibleCategories = useMemo(() => {
    const known = CATEGORY_ORDER.filter((category) =>
      providersByCategory.has(category)
    );
    const extras = Array.from(providersByCategory.keys()).filter(
      (category) =>
        !CATEGORY_ORDER.includes(category as IntegrationCategory)
    );

    return [...known, ...extras];
  }, [providersByCategory]);

  useEffect(() => {
    if (visibleCategories.length === 0) {
      setActiveCategory("");
      return;
    }

    setActiveCategory((current) =>
      current && visibleCategories.includes(current)
        ? current
        : visibleCategories[0]
    );
  }, [visibleCategories]);

  const categoryLabel = (category: string): string => {
    const key = `integration.categories.${category}`;
    const translated = t(key);

    return translated !== key ? translated : category;
  };

  const capabilityLabel = (capability: string): string => {
    const key = `studio.integrations.capabilities.${capability}`;
    const translated = t(key);

    return translated !== key ? translated : capability;
  };

  const openConnectModal = (provider: IntegrationProvider): void => {
    setSelectedProvider(provider);
    setSelectedIntegration(null);
    setModalOpen(true);
  };

  const openConfigureModal = (
    provider: IntegrationProvider,
    integration: TenantIntegration
  ): void => {
    setSelectedProvider(provider);
    setSelectedIntegration(integration);
    setModalOpen(true);
  };

  const closeModal = (): void => {
    setModalOpen(false);
    setSelectedProvider(null);
    setSelectedIntegration(null);
    setDeepLinkUnitId(null);
    setAutoClaim(false);
  };

  const handleSaved = async (): Promise<void> => {
    const wasEdit = selectedIntegration !== null;
    const connectedGoogle =
      !wasEdit && selectedProvider?.slug === "google-reserve";
    const helpUrl = getHelpArticleUrl(
      HELP_ARTICLE_GOOGLE_BOOKINGS,
      i18n.language
    );

    await loadData();

    if (connectedGoogle) {
      toast.success(
        <span>
          {t("studio.integrations.googleReserve.connectSuccess")}{" "}
          <a href={helpUrl} target="_blank" rel="noreferrer">
            {t("studio.integrations.googleReserve.connectSuccessHelpLink")}
          </a>
        </span>,
        { autoClose: 12000 }
      );
      return;
    }

    toast.success(
      wasEdit
        ? t("studio.integrations.toast.updateSuccess")
        : t("studio.integrations.toast.installSuccess")
    );
  };

  const handleDisconnected = (): void => {
    loadData();
    toast.success(t("studio.integrations.toast.uninstallSuccess"));
  };

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb
          title={t("studio.integrations.breadcrumb.title")}
          pageTitle={t("studio.integrations.breadcrumb.pageTitle")}
        />
        <div className="d-flex justify-content-end mb-3">
          <HelpArticleLink articlePath={HELP_ARTICLE_SETTINGS_INTEGRATIONS} />
        </div>

        <Row className="mb-3">
          <Col>
            <p className="text-muted mb-0">
              {t("studio.integrations.card.subtitle")}
            </p>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary">{t("studio.integrations.loading")}</Spinner>
          </div>
        ) : providers.length === 0 ? (
          <Card className="border border-primary border-opacity-10 shadow-none">
            <CardBody className="text-center py-5 text-muted">
              {t("studio.integrations.empty.noProviders")}
            </CardBody>
          </Card>
        ) : (
          <Card className="border border-primary border-opacity-10 shadow-none">
            <CardBody>
              <Nav
                className="nav-tabs nav-tabs-custom nav-success mb-4"
                role="tablist"
              >
                {visibleCategories.map((category) => (
                  <NavItem key={category}>
                    <NavLink
                      className={classnames({ active: activeCategory === category })}
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        setActiveCategory(category);
                      }}
                    >
                      <i
                        className={`${categoryIcon(category)} me-1 align-bottom`}
                      />
                      {categoryLabel(category)}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={activeCategory}>
                {visibleCategories.map((category) => (
                  <TabPane key={category} tabId={category}>
                    <Row className="g-4">
                      {(providersByCategory.get(category) ?? []).map((provider) => (
                        <ProviderCard
                          key={provider.slug}
                          provider={provider}
                          providerInstallations={
                            installedByProvider.get(provider.slug) ?? []
                          }
                          onConnect={openConnectModal}
                          onConfigure={openConfigureModal}
                          capabilityLabel={capabilityLabel}
                        />
                      ))}
                    </Row>
                  </TabPane>
                ))}
              </TabContent>
            </CardBody>
          </Card>
        )}
      </Container>

      <IntegrationConfigModal
        isOpen={modalOpen}
        provider={selectedProvider}
        installedIntegration={selectedIntegration}
        units={units}
        onClose={closeModal}
        onSaved={handleSaved}
        onUninstalled={handleDisconnected}
        deepLinkUnitId={deepLinkUnitId}
        autoClaim={autoClaim}
      />
    </div>
  );
};

export default IntegrationsPage;
